const LETTER_BUILD_STATUSES = new Set(['not_started', 'building', 'ready', 'failed']);
const MAILING_BUILD_STATUSES = new Set(['not_started', 'building', 'ready', 'failed']);
const CAMPAIGN_BUILD_STATUSES = new Set(['building', 'ready', 'failed']);
const LETTER_STATUSES = new Set(['not_generated', 'generating', 'generated', 'failed']);
const LETTER_ARCHIVE_STATUSES = new Set(['not_started', 'building', 'ready', 'failed']);

function invalidResponse(code = 'invalid-argument') {
  const err = new Error('invalid_campaign_letter_response');
  err.code = code;
  return err;
}

function assertNoSensitiveLetterMaterial(value) {
  const json = JSON.stringify(value);
  if (
    json.includes('storagePath') ||
    json.includes('tokenHash') ||
    json.includes('contentHash') ||
    json.includes('publicAccess')
  ) {
    throw invalidResponse('internal');
  }
}

export function parsePrepareAdminProspectCampaignMailingsResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  if (!MAILING_BUILD_STATUSES.has(data.mailingBuildStatus)) throw invalidResponse();
  const out = {
    campaignId,
    mailingBuildStatus: data.mailingBuildStatus,
    mailingCountExpected: Number.isInteger(data.mailingCountExpected) ? data.mailingCountExpected : null,
    mailingCount: Number.isInteger(data.mailingCount) ? data.mailingCount : 0,
  };
  if (data.duplicate === true) out.duplicate = true;
  return out;
}

export function parseRetryAdminProspectCampaignBuildResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  if (data.buildStatus != null && !CAMPAIGN_BUILD_STATUSES.has(data.buildStatus)) {
    throw invalidResponse();
  }
  const out = {
    campaignId,
    buildStatus: typeof data.buildStatus === 'string' ? data.buildStatus : null,
    recovered: data.recovered === true,
    duplicate: data.duplicate === true,
    exhausted: data.exhausted === true,
  };
  if (typeof data.failureCode === 'string' && data.failureCode) {
    out.failureCode = data.failureCode;
  }
  if (data.buildRecoverable === true) out.buildRecoverable = true;
  return out;
}

export function parseRetryAdminProspectCampaignMailingBuildResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  const out = {
    campaignId,
    mailingBuildStatus: typeof data.mailingBuildStatus === 'string' ? data.mailingBuildStatus : null,
    recovered: data.recovered === true,
    duplicate: data.duplicate === true,
    exhausted: data.exhausted === true,
  };
  if (typeof data.failureCode === 'string' && data.failureCode) out.failureCode = data.failureCode;
  if (data.mailingBuildRecoverable === true) out.mailingBuildRecoverable = true;
  return out;
}

function parseSafeLetterArtifact(artifact) {
  if (!artifact || typeof artifact !== 'object' || Array.isArray(artifact)) return null;
  const out = {
    version: Number.isInteger(artifact.version) ? artifact.version : null,
    templateVersion: Number.isInteger(artifact.templateVersion) ? artifact.templateVersion : null,
    language: typeof artifact.language === 'string' ? artifact.language : null,
    byteSize: Number.isInteger(artifact.byteSize) ? artifact.byteSize : null,
    generatedAt:
      typeof artifact.generatedAt === 'string' || artifact.generatedAt == null
        ? artifact.generatedAt || null
        : null,
  };
  if (typeof artifact.templateId === 'string') out.templateId = artifact.templateId;
  if (Number.isInteger(artifact.schemaVersion)) out.schemaVersion = artifact.schemaVersion;
  if (typeof artifact.bilingual === 'boolean') out.bilingual = artifact.bilingual;
  return out;
}

function parseMailingListItem(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const mailingId = typeof item.mailingId === 'string' ? item.mailingId.trim() : '';
  if (!mailingId) return null;
  const out = {
    mailingId,
    targetId: typeof item.targetId === 'string' ? item.targetId : null,
    prospectId: typeof item.prospectId === 'string' ? item.prospectId : null,
    targetSequence: Number.isInteger(item.targetSequence) ? item.targetSequence : 0,
    recipientOrdinal: Number.isInteger(item.recipientOrdinal) ? item.recipientOrdinal : 0,
    property: item.property && typeof item.property === 'object' ? item.property : null,
    recipient: item.recipient && typeof item.recipient === 'object' ? item.recipient : null,
    letterStatus: LETTER_STATUSES.has(item.letterStatus) ? item.letterStatus : 'not_generated',
    qrStatus: typeof item.qrStatus === 'string' ? item.qrStatus : null,
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : null,
  };
  const letterArtifact = parseSafeLetterArtifact(item.letterArtifact);
  if (letterArtifact) out.letterArtifact = letterArtifact;
  if (item.publicResponse && typeof item.publicResponse === 'object' && !Array.isArray(item.publicResponse)) {
    const receivedIntents =
      item.publicResponse.receivedIntents && typeof item.publicResponse.receivedIntents === 'object'
        ? item.publicResponse.receivedIntents
        : {};
    out.publicResponse = {
      receivedIntents,
      legacyReceived: item.publicResponse.legacyReceived === true,
    };
  }
  return out;
}

export function parseGenerateAdminProspectCampaignLettersResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  if (!LETTER_BUILD_STATUSES.has(data.letterBuildStatus)) throw invalidResponse();
  return {
    campaignId,
    letterBuildStatus: data.letterBuildStatus,
    letterCountExpected: Number.isInteger(data.letterCountExpected) ? data.letterCountExpected : null,
    letterCountGenerated: Number.isInteger(data.letterCountGenerated) ? data.letterCountGenerated : 0,
    duplicate: data.duplicate === true,
  };
}

export function parseListAdminProspectCampaignMailingsResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  const source = Array.isArray(data.mailings) ? data.mailings : [];
  const mailings = source.map(parseMailingListItem).filter(Boolean);
  return {
    campaignId,
    mailings,
    pageSize: Number.isInteger(data.pageSize) ? data.pageSize : mailings.length,
    nextPageToken: typeof data.nextPageToken === 'string' && data.nextPageToken ? data.nextPageToken : null,
  };
}

export function parseRequestAdminProspectCampaignLetterArchiveResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  if (!LETTER_ARCHIVE_STATUSES.has(data.letterArchiveStatus)) throw invalidResponse();
  return {
    campaignId,
    letterArchiveStatus: data.letterArchiveStatus,
    letterArchiveCountExpected: Number.isInteger(data.letterArchiveCountExpected)
      ? data.letterArchiveCountExpected
      : null,
    letterArchiveCountGenerated: Number.isInteger(data.letterArchiveCountGenerated)
      ? data.letterArchiveCountGenerated
      : 0,
    letterArchiveProgressPercent: Number.isInteger(data.letterArchiveProgressPercent)
      ? data.letterArchiveProgressPercent
      : null,
    letterArchiveRecoverable: data.letterArchiveRecoverable === true,
    duplicate: data.duplicate === true,
  };
}

export function parseRetryAdminProspectCampaignLetterArchiveResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const campaignId = typeof data.campaignId === 'string' ? data.campaignId.trim() : '';
  if (!campaignId) throw invalidResponse();
  if (!LETTER_ARCHIVE_STATUSES.has(data.letterArchiveStatus)) throw invalidResponse();
  return {
    campaignId,
    letterArchiveStatus: data.letterArchiveStatus,
    letterArchiveCountExpected: Number.isInteger(data.letterArchiveCountExpected)
      ? data.letterArchiveCountExpected
      : null,
    letterArchiveCountGenerated: Number.isInteger(data.letterArchiveCountGenerated)
      ? data.letterArchiveCountGenerated
      : 0,
    letterArchiveProgressPercent: Number.isInteger(data.letterArchiveProgressPercent)
      ? data.letterArchiveProgressPercent
      : null,
    letterArchiveRecoverable: data.letterArchiveRecoverable === true,
    recovered: data.recovered === true,
    duplicate: data.duplicate === true,
    exhausted: data.exhausted === true,
  };
}

export function parseGetAdminProspectCampaignLetterArchiveDownloadUrlResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const url = typeof data.url === 'string' ? data.url.trim() : '';
  if (!/^https:\/\//i.test(url)) throw invalidResponse();
  const expiresAt = typeof data.expiresAt === 'string' ? data.expiresAt : '';
  if (!expiresAt || Number.isNaN(Date.parse(expiresAt))) throw invalidResponse();
  const fileName = typeof data.fileName === 'string' ? data.fileName : '';
  if (!fileName || fileName.length > 200 || fileName !== 'rentalyzer-campaign-letters.zip') {
    throw invalidResponse();
  }
  return { url, expiresAt, fileName };
}

export function parseGetAdminProspectCampaignLetterDownloadUrlResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw invalidResponse();
  assertNoSensitiveLetterMaterial(data);
  const url = typeof data.url === 'string' ? data.url.trim() : '';
  if (!/^https:\/\//i.test(url)) throw invalidResponse();
  const expiresAt = typeof data.expiresAt === 'string' ? data.expiresAt : '';
  if (!expiresAt || Number.isNaN(Date.parse(expiresAt))) throw invalidResponse();
  const fileName = typeof data.fileName === 'string' ? data.fileName : '';
  // Same contract as functions/admin/prospectCampaignLetters.js isSafeLetterDownloadFileName.
  if (
    !fileName ||
    fileName.length > 200 ||
    /[\u0000-\u001F\u007F]/.test(fileName) ||
    !/^rentalyzer-campaign-letter(?:-[0-9]+-[0-9]+)?\.pdf$/.test(fileName)
  ) {
    throw invalidResponse();
  }
  return { url, expiresAt, fileName };
}
